import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useApiCall } from "../hooks/useApiCall";
import { AthleteInfo } from "../api/models/athlete";
import { getAthleteApi, updateAthleteApi } from "../api/athleteApi";

interface AthleteContextType {
    athlete: AthleteInfo | null;
    loading: boolean;
    error?: string;
    updateAthlete: (athlete: AthleteInfo) => Promise<void>;
}

interface AthleteProviderProps {
    athleteGuid: string;
    children: ReactNode;
}

const AthleteContext = createContext<AthleteContextType | undefined>(undefined);

export function useAthlete() {
    const context = useContext(AthleteContext);
    if (!context) {
        throw new Error('useAthlete must be used within AthleteProvider');
    }
    return context;
}

export function AthleteProvider({ athleteGuid, children }: AthleteProviderProps) {
    const getAthleteApiRequest = useApiCall(getAthleteApi);
    const updateAthleteApiRequest = useApiCall(updateAthleteApi);
    const [athlete, setAthlete] = useState<AthleteInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | undefined>(undefined);

    useEffect(() => {
        async function fetchAthlete() {
            setLoading(true);
            setError(undefined);
            try {
                const response = await getAthleteApiRequest.request(athleteGuid);
                if (response) {
                    setAthlete(response);
                } else {
                    setAthlete(null);
                }
            } catch (error: any) {
                setError(error?.message || 'Failed to fetch member');
                setAthlete(null);
            } finally {
                setLoading(false);
            }
        }

        fetchAthlete();

    }, [athleteGuid]);

    const updateAthlete = async (updateAthleteInfo: AthleteInfo) => {
        setLoading(true);
        setError(undefined);
        try {

            const response = await updateAthleteApiRequest.request(athlete!.id, updateAthleteInfo);
            setAthlete(response);
        } catch (error: any) {
            setError(error?.message || 'Failed to update member');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AthleteContext.Provider value={{ athlete, loading, error, updateAthlete }}>
            {children}
        </AthleteContext.Provider>
    );
}